/**
 * §5.K — L'anneau de notation.
 *
 * ⛔ L'UN DES SIX EMPLACEMENTS D'AFFICHAGE DE LA NOTE, et il obéit à la même
 * règle que les cinq autres : sur un dossier sans matière, on n'affiche PAS
 * un anneau à zéro — un anneau vide se lit comme « très mauvais » alors que la
 * vérité est « rien à évaluer ».
 */
export function AnneauNotation({
  evaluable,
  note,
  cote,
  motif,
  titre,
  sousTitre,
  gravites,
}: {
  evaluable: boolean;
  note: number | null;
  cote: string;
  motif: string | null;
  titre: string;
  sousTitre?: string;
  gravites?: { critique: number; majeur: number; moyen: number };
}) {
  const rayon = 52;
  const circonference = 2 * Math.PI * rayon;
  const part = evaluable && note !== null ? note / 20 : 0;

  // La couleur suit la cote, pas la note brute : c'est la cote qui est lue.
  const couleur = !evaluable
    ? "var(--gris)"
    : cote.startsWith("A")
      ? "var(--vert)"
      : cote.startsWith("BBB") || cote.startsWith("BB")
        ? "var(--or)"
        : "var(--rouge)";

  return (
    <section className="anneau-carte">
      <div className="anneau" role="img" aria-label={evaluable ? `${note} sur 20, cote ${cote}` : "Non évalué"}>
        <svg viewBox="0 0 128 128" width="128" height="128">
          <circle cx="64" cy="64" r={rayon} fill="none" stroke="var(--bord)" strokeWidth="11" />
          {evaluable && (
            <circle
              cx="64"
              cy="64"
              r={rayon}
              fill="none"
              stroke={couleur}
              strokeWidth="11"
              strokeLinecap="round"
              strokeDasharray={`${circonference * part} ${circonference}`}
              transform="rotate(-90 64 64)"
            />
          )}
        </svg>

        <div className="anneau__centre">
          {evaluable ? (
            <>
              <span className="anneau__note">{Math.round(note!)}</span>
              <span className="anneau__sur">/ 20</span>
              <span className="anneau__cote" style={{ color: couleur }}>{cote}</span>
            </>
          ) : (
            <span className="anneau__non-evalue">NON<br />ÉVALUÉ</span>
          )}
        </div>
      </div>

      <div className="anneau-carte__texte">
        <h2>{titre}</h2>
        {sousTitre && <p className="anneau-carte__sous-titre">{sousTitre}</p>}

        {evaluable && gravites ? (
          <div className="pastilles">
            <span className={gravites.critique ? "pastille pastille--critique" : "pastille"}>
              {gravites.critique} critique{gravites.critique > 1 ? "s" : ""}
            </span>
            <span className={gravites.majeur ? "pastille pastille--majeur" : "pastille"}>
              {gravites.majeur} majeur{gravites.majeur > 1 ? "s" : ""}
            </span>
            <span className={gravites.moyen ? "pastille pastille--moyen" : "pastille"}>
              {gravites.moyen} moyen{gravites.moyen > 1 ? "s" : ""}
            </span>
          </div>
        ) : (
          !evaluable && (
            <>
              <p className="anneau-carte__motif">{motif}</p>
              <p className="anneau-carte__consequence">
                Visa réservé — aucune cote ne peut être prononcée.
              </p>
            </>
          )
        )}
      </div>
    </section>
  );
}

/** Barres de conformité par domaine, sur 20. */
export function BarresDomaines({
  domaines,
}: {
  domaines: readonly { domaine: string; libelle: string; note: number; ponderation: number }[];
}) {
  if (domaines.length === 0) return <p className="a-faire">Domaines non évalués.</p>;

  return (
    <ul className="domaines">
      {domaines.map((d) => {
        const part = d.note / 20;
        const couleur = part >= 0.85 ? "var(--vert)" : part >= 0.6 ? "var(--or)" : "var(--rouge)";

        return (
          <li key={d.domaine}>
            <span className="domaines__libelle">
              {d.domaine} — {d.libelle}
              <em> ({(d.ponderation * 100).toFixed(0)} %)</em>
            </span>
            <span className="domaines__barre">
              <span style={{ width: `${part * 100}%`, background: couleur }} />
            </span>
            <span className="domaines__note">{d.note.toFixed(0)}/20</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * §5.D — La chaîne de traitement du mois.
 * C'est elle qui montre OÙ LE DOSSIER BLOQUE : chaque étape porte sa valeur réelle.
 */
export function ChaineTraitement({
  etapes,
}: {
  etapes: readonly { libelle: string; statut: string; valeur: string }[];
}) {
  return (
    <ol className="chaine-h">
      {etapes.map((e, i) => (
        <li key={e.libelle} className={`chaine-h__etape chaine-h__etape--${e.statut}`}>
          {i > 0 && <span className="chaine-h__lien" aria-hidden />}
          <span className="chaine-h__pastille" aria-hidden>
            {e.statut === "fait" ? "✓" : e.statut === "partiel" ? "!" : ""}
          </span>
          <span className="chaine-h__libelle">{e.libelle}</span>
          <span className="chaine-h__valeur">{e.valeur}</span>
        </li>
      ))}
    </ol>
  );
}
